import React, { useState, useRef, useEffect, useCallback } from "react";
import { SPARKY_SERVER_URL, LUMI_SAFE_SYSTEM, MOODS } from '../shared/constants';
import { getSubjectDisplayName } from '../../utils/subjectNames';
import { LumiMarkdown } from '../shared/ui';
import { isSafeInput, getSafeRedirect, speakText, detectTopic, detectDiagram } from './helpers';
import { LumiOrb, LUMI_STATES } from '../LumiOrb';
// the chat tutor's moods, shown on the purple orb
const ORB_STATE = { thinking: LUMI_STATES.THINKING, correct: LUMI_STATES.CELEBRATING, celebrate: LUMI_STATES.CELEBRATING, excited: LUMI_STATES.CELEBRATING, wrong: LUMI_STATES.WRONG, confused: LUMI_STATES.STUCK, listening: LUMI_STATES.LISTENING };
import VoiceWave from './VoiceWave';
import TopicExplainer from './TopicExplainer';
import LumiDiagram from './LumiDiagram';
import { supabase } from '../../lib/supabase';

async function getAuthHeader() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {};
  } catch { return {}; }
}


export default function LumiTutor({
  subject = "",
  yearLevel = "",
  lastQuestion = "",
  lastAnswer = "",
  wasCorrect = null,
  onClose,
  theme = {},
}) {
  const T = {
    bg:      theme.bg      || "#05070D",
    surface: theme.surface || "#0D1017",
    card:    theme.card    || "#12161F",
    border:  theme.border  || "#1A2035",
    text:    theme.text    || "#F1F5F9",
    textMid: theme.textMid || "#94A3B8",
    accent:  theme.accent  || "#0D9488",
    ...theme,
  };

  const [mood, setMood] = useState("idle");
  const [talking, setTalking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [speechBubble, setSpeechBubble] = useState("");
  const [showBubble, setShowBubble] = useState(false);
  const [isListening, setIsListening]   = useState(false);
  const [isSpeaking, setIsSpeaking]     = useState(false);
  const [voiceMode, setVoiceMode]       = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef                  = useRef(null);
  const [activeTopic, setActiveTopic] = useState(null);
  const [lastExplainStyle, setLastExplainStyle] = useState('default');
  const [lastExplainTopic, setLastExplainTopic] = useState(null);
  const bottomRef = useRef(null);
  const talkTimerRef = useRef(null);
  const sendMessageRef = useRef(null);
  const messagesRef   = useRef([]);

  const EXPLAIN_STYLES = ['story', 'realworld', 'analogy', 'stepbystep', 'examplefirst'];

  function isConfused(text) {
    const t = text.toLowerCase();
    return t.includes("don't get it") || t.includes("dont get it") ||
      t.includes("don't understand") || t.includes("dont understand") ||
      t.includes("confused") || t.includes("still don't") || t.includes("still dont") ||
      t.includes("explain differently") || t.includes("try again") ||
      t.includes("not sure") || t.includes("what does that mean") ||
      t.includes("i don't know") || t.includes("can you show") ||
      t.includes("huh") || t.includes("what?");
  }

  function getNextStyle(currentStyle) {
    const idx = EXPLAIN_STYLES.indexOf(currentStyle);
    return EXPLAIN_STYLES[(idx + 1) % EXPLAIN_STYLES.length];
  }

  function buildRetryPrompt(topic, style) {
    const styleInstructions = {
      story: `Explain ${topic || 'this concept'} using a short, engaging story or narrative that a child can picture. Start with "Imagine..." or "Once there was...". Make it fun and relatable.`,
      realworld: `Explain ${topic || 'this concept'} by showing exactly where it appears in real life \u2014 construction, sport, cooking, gaming, etc. Make it feel immediately useful.`,
      analogy: `Explain ${topic || 'this concept'} using a simple, clever analogy comparing it to something a child already knows well. Start with "Think of it like...".`,
      stepbystep: `Explain ${topic || 'this concept'} in tiny numbered steps. Use very simple language. Do just 3-4 steps maximum. Put one idea per step.`,
      examplefirst: `Show a simple worked example of ${topic || 'this concept'} FIRST, then explain the rule. Numbers should be small and easy.`,
    };
    return styleInstructions[style] || `Explain ${topic || 'this concept'} in a completely different, simpler way.`;
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    window.speechSynthesis?.getVoices();
    setTimeout(() => window.speechSynthesis?.getVoices(), 300);
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(!!SpeechRecognition);
  }, []);

  function startListening() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-AU";

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setIsListening(false);
      if (transcript.trim()) {
        setInput(transcript);
        setTimeout(() => sendMessageRef.current?.(transcript), 300);
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === "not-allowed") {
        alert("Microphone access is needed for voice mode. Please allow microphone access in your browser settings.");
      }
    };

    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  }

  function stopListening() {
    recognitionRef.current?.stop();
    setIsListening(false);
  }

  async function lumiSpeak(text) {
    if (!voiceMode || !text) return;
    setIsSpeaking(true);

    const plainText = text
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/#{1,3}\s/g, '')
      .replace(/^[-\u2022]\s/gm, '')
      .slice(0, 300);

    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(plainText);
      utt.rate = 0.92;
      utt.pitch = 1.1;
      utt.volume = 1;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(v =>
        v.name.includes('Samantha') || v.name.includes('Karen') ||
        v.name.includes('Moira') || v.name.includes('Tessa') ||
        (v.lang === 'en-AU') || (v.lang === 'en-GB')
      ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
      if (preferred) utt.voice = preferred;
      utt.onend = () => {
        setIsSpeaking(false);
        if (voiceMode) setTimeout(() => startListening(), 500);
      };
      utt.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utt);
      return;
    }

    try {
      const authHeader = await getAuthHeader();
      const res = await fetch(SPARKY_SERVER_URL + "/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ text: plainText }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => {
          URL.revokeObjectURL(url);
          setIsSpeaking(false);
          if (voiceMode) setTimeout(() => startListening(), 500);
        };
        audio.onerror = () => setIsSpeaking(false);
        audio.play();
      } else {
        setIsSpeaking(false);
      }
    } catch(e) {
      setIsSpeaking(false);
    }
  }

  const lumiSay = useCallback((text, moodKey = "explaining") => {
    setMood(moodKey);
    setSpeechBubble(text.length > 80 ? text.slice(0, 80) + "\u2026" : text);
    setShowBubble(true);
    setTimeout(() => setShowBubble(false), Math.min(text.length * 60 + 1000, 5000));

    clearInterval(talkTimerRef.current);
    setTalking(true);
    speakText(text,
      () => setTalking(true),
      () => {
        setTalking(false);
        clearInterval(talkTimerRef.current);
      }
    );
  }, []);

  const triggerMood = useCallback((moodKey) => {
    const m = MOODS[moodKey];
    if (!m) return;
    setMood(moodKey);
    if (m.speech) {
      setTimeout(() => lumiSay(m.speech, moodKey), 300);
    }
  }, [lumiSay]);

  useEffect(() => {
    if (wasCorrect === true) triggerMood("correct");
    else if (wasCorrect === false) triggerMood("wrong");
  }, [wasCorrect]);

  useEffect(() => {
    const displaySubject = subject ? getSubjectDisplayName(subject) : '';
    const welcome = displaySubject
      ? `I'm Lumi, your Empirio tutor. Let's work on ${displaySubject} together!`
      : "I'm Lumi, your Empirio AI tutor. Ask me anything!";
    setTimeout(() => {
      setMessages(m => m.length > 0 ? m : [{ from: "lumi", text: welcome, topic: null }]);
      lumiSay(welcome, "happy");
    }, 400);
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;
    window.speechSynthesis?.cancel();
    setTalking(false);

    if (!isSafeInput(text)) {
      const redirect = getSafeRedirect();
      setMessages(m => [...m, { from: "user", text }, { from: "lumi", text: redirect, topic: null }]);
      setInput("");
      lumiSay(redirect, "happy");
      if (voiceMode) lumiSpeak(redirect);
      return;
    }

    const topic = detectTopic(text) || lastExplainTopic;
    setMessages(m => [...m, { from: "user", text }]);
    setInput("");
    setLoading(true);
    setMood("thinking");

    const confused = isConfused(text);
    const nextStyle = confused ? getNextStyle(lastExplainStyle) : 'default';
    if (confused) setLastExplainStyle(nextStyle);
    if (topic) setLastExplainTopic(topic);

    try {
      const context = [
        subject && `Subject: ${subject}`,
        yearLevel && `Year level: ${yearLevel}`,
        lastQuestion && `Recent question: ${lastQuestion}`,
        lastAnswer && `Student answered: ${lastAnswer}`,
        wasCorrect === false && `The student got this wrong. Provide encouragement calibrated to difficulty.`,
        confused && nextStyle !== 'default' && `INSTRUCTION: The student is confused. Use the ${nextStyle.toUpperCase()} explanation style: ${buildRetryPrompt(topic, nextStyle)}`,
      ].filter(Boolean).join("\n");

      const systemPrompt = LUMI_SAFE_SYSTEM + (context ? `\n\nStudent context:\n${context}` : "");

      const history = [];
      const recentMsgs = messagesRef.current.slice(-6);
      for (const msg of recentMsgs) {
        history.push({ role: msg.from === 'user' ? 'user' : 'assistant', content: msg.text });
      }
      history.push({ role: 'user', content: confused ? `I still don't understand. Please try explaining in a completely different way.` : text });

      const chatAuthHeader = await getAuthHeader();
      const response = await fetch(`${SPARKY_SERVER_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...chatAuthHeader },
        body: JSON.stringify({
          model: "claude-haiku-5-5",
          max_tokens: 250,
          system: systemPrompt,
          messages: history,
        }),
      });

      if (response.status === 401) {
        setLoading(false);
        const msg = "Please log in again to continue chatting with me.";
        setMessages(m => [...m, { from: "lumi", text: msg, topic: null }]);
        lumiSay(msg, "sad");
        return;
      }
      if (response.status === 429) {
        setLoading(false);
        const msg = "We've been chatting a lot today! Come back in an hour and I'll be ready to help again.";
        setMessages(m => [...m, { from: "lumi", text: msg, topic: null }]);
        lumiSay(msg, "sad");
        return;
      }

      const data = await response.json();
      const reply = data?.content?.[0]?.text || "I am not sure about that one. Can you try asking in a different way?";

      setLoading(false);
      const diagramTopic = detectDiagram(reply);
      const finalTopic = topic || (diagramTopic ? diagramTopic + ' diagram' : null);
      if (finalTopic && !lastExplainTopic) setLastExplainTopic(finalTopic);
      setMessages(m => [...m, { from: "lumi", text: reply, topic: finalTopic, canRetry: true }]);
      lumiSay(reply, "explaining");
      if (voiceMode) lumiSpeak(reply);
      if (topic) setActiveTopic(topic);

    } catch {
      setLoading(false);
      const fallback = "I am having trouble connecting right now. Try again in a moment!";
      setMessages(m => [...m, { from: "lumi", text: fallback, topic: null }]);
      lumiSay(fallback, "sad");
    }
  }, [subject, yearLevel, lastQuestion, lastAnswer, lumiSay]);

  // Keep ref in sync so startListening can always call the latest sendMessage
  sendMessageRef.current = sendMessage;

  const moodInfo = MOODS[mood] || MOODS.idle;

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100%", minHeight: 500,
      background: T.bg, fontFamily: "'Plus Jakarta Sans', sans-serif",
      color: T.text, position: "relative", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: moodInfo.color,
            boxShadow: `0 0 8px ${moodInfo.color}`,
            transition: "background 0.3s ease, box-shadow 0.3s ease",
          }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Lumi</div>
            <div style={{ fontSize: 10, color: "#10B981", display: "flex", alignItems: "center", gap: 4 }}>
              {talking ? <><VoiceWave active color="#10B981" /> Speaking...</> : `\u25CF ${moodInfo.label}`}
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: "none", border: "none", color: T.textMid,
            fontSize: 18, cursor: "pointer", padding: "4px 8px",
          }}>{"\u2715"}</button>
        )}
      </div>

      {/* Lumi Stage */}
      <div style={{
        background: `linear-gradient(180deg, #0A0E1A 0%, ${T.bg} 100%)`,
        padding: "20px 16px 12px",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        flexShrink: 0, position: "relative", minHeight: 200,
      }}>
        <div style={{
          position: "absolute", width: 220, height: 220, borderRadius: "50%",
          background: `radial-gradient(circle, ${moodInfo.color}15 0%, transparent 70%)`,
          bottom: -60, left: "50%", transform: "translateX(-50%)",
          transition: "background 0.3s ease", pointerEvents: "none",
        }} />

        {showBubble && speechBubble && (
          <div style={{
            position: "absolute", top: 10, left: "55%",
            background: "#FBBF24", borderRadius: "16px 16px 16px 4px",
            padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#1A0A00",
            maxWidth: 160, lineHeight: 1.4, zIndex: 10,
            boxShadow: "0 4px 16px rgba(251,191,36,0.3)",
            animation: "fadeIn 0.3s ease",
          }}>
            {speechBubble}
            <div style={{
              position: "absolute", bottom: -7, left: 10,
              border: "7px solid transparent", borderTopColor: "#FBBF24", borderBottom: "none",
            }} />
          </div>
        )}

        <LumiOrb state={talking ? LUMI_STATES.LISTENING : (ORB_STATE[mood] || LUMI_STATES.IDLE)} size={150} />

        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(90deg, transparent, rgba(251,191,36,0.3), transparent)",
        }} />
      </div>

      {activeTopic && (
        <div style={{ padding: "0 14px", flexShrink: 0 }}>
          <TopicExplainer topic={activeTopic} key={activeTopic} />
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "12px 14px",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: msg.from === "user" ? "flex-end" : "flex-start",
            gap: 8, animation: "fadeIn 0.3s ease",
          }}>
            {msg.from === "lumi" && (
              <div style={{
                width: 26, height: 26, borderRadius: "50%",
                background: "linear-gradient(135deg, #FBBF24, #F59E0B)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 800, flexShrink: 0, marginTop: 4,
                color: "#1A0A00",
              }}>S</div>
            )}
            <div style={{
              background: msg.from === "user" ? T.accent : T.card,
              border: msg.from === "lumi" ? `1px solid ${T.border}` : "none",
              borderRadius: msg.from === "user" ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
              padding: "10px 13px", fontSize: 13, lineHeight: 1.55,
              maxWidth: "78%", color: T.text,
            }}>
              <LumiMarkdown text={msg.text} />
              {msg.from === 'lumi' && detectDiagram(msg.text) && (
                <LumiDiagram type={detectDiagram(msg.text)} />
              )}
              {msg.from === 'lumi' && msg.canRetry && i === messages.length - 1 && (
                <button
                  onClick={() => sendMessage("I don't understand, can you explain it differently?")}
                  style={{ marginTop:8, padding:"6px 12px", background:"none", border:`1px solid ${T.accent}55`,
                    borderRadius:8, color:T.accent, fontSize:11, fontWeight:600, cursor:"pointer",
                    fontFamily:"'Plus Jakarta Sans',sans-serif", display:"flex", alignItems:"center", gap:5 }}>
                  {"\uD83D\uDD04"} Try a different explanation
                </button>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: "linear-gradient(135deg, #FBBF24, #F59E0B)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 800, color: "#1A0A00",
            }}>S</div>
            <div style={{
              background: T.card, border: `1px solid ${T.border}`,
              borderRadius: "4px 16px 16px 16px", padding: "12px 14px",
              display: "flex", gap: 4, alignItems: "center",
            }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: "50%", background: "#FBBF24",
                  animation: `lumi-idle ${0.6 + i * 0.15}s ease-in-out infinite`,
                }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick prompts */}
      <div style={{
        display: "flex", gap: 6, padding: "6px 14px",
        overflowX: "auto", flexShrink: 0, borderTop: `1px solid ${T.border}`,
      }}>
        {(() => {
          const subj = String(subject || "").toLowerCase();
          const hasQuestion = !!(lastQuestion && String(lastQuestion).trim());
          let chips;
          if (hasQuestion) {
            chips = ["Why was I wrong?", "Explain this differently", "Can you show me how?", "What should I remember?"];
          } else if (/bio|chem|phys|science/.test(subj)) {
            chips = ["Explain this concept", "Give me a worked example", "What does this mean for the exam?", "Show me a diagram"];
          } else if (/math|numer/.test(subj)) {
            chips = ["Show me the working", "Give me another example", "What's the formula?", "Where do students go wrong?"];
          } else if (/english|reading|writing|language/.test(subj)) {
            chips = ["What is this text about?", "How do I identify the main idea?", "What does this word mean?", "How do I answer inference questions?"];
          } else {
            chips = ["Explain this to me", "Give me an example", "Why does this matter?", "How do I remember this?"];
          }
          return chips;
        })().map(prompt => (
          <button key={prompt} onClick={() => sendMessage(prompt)} style={{
            background: T.card, border: `1px solid ${T.border}`,
            borderRadius: 20, padding: "6px 12px", fontSize: 11,
            color: T.textMid, cursor: "pointer", whiteSpace: "nowrap",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            flexShrink: 0,
          }}>{prompt}</button>
        ))}
      </div>

      {/* Input */}
      <div style={{ flexShrink: 0 }}>
        {voiceMode && (
          <div style={{ background: isListening ? "#EF444420" : isSpeaking ? "#0D948820" : "#0D948810", borderTop: `1px solid ${T.border}`, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: isListening ? "#EF4444" : isSpeaking ? T.accent : "#9CA3AF", animation: (isListening || isSpeaking) ? "pulse 1s infinite" : "none" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: isListening ? "#EF4444" : isSpeaking ? T.accent : "#9CA3AF" }}>
              {isListening ? "Listening... speak now" : isSpeaking ? "Lumi is speaking..." : "Voice mode \u2014 tap \uD83C\uDFA4 to speak"}
            </span>
            {isListening && <button onClick={stopListening} style={{ background: "#EF4444", border: "none", borderRadius: 6, padding: "4px 10px", color: "white", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Stop</button>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, padding: "10px 14px 16px", background: T.surface, borderTop: `1px solid ${T.border}` }}>
          {voiceSupported && (
            <button
              onClick={() => {
                if (voiceMode) {
                  setVoiceMode(false);
                  stopListening();
                } else {
                  setVoiceMode(true);
                  setTimeout(() => startListening(), 300);
                }
              }}
              title={voiceMode ? "Turn off voice mode" : "Turn on voice mode"}
              style={{ background: voiceMode ? T.accent : T.card, border: `1px solid ${voiceMode ? T.accent : T.border}`, borderRadius: "50%", width: 44, height: 44, color: voiceMode ? "white" : T.textMid, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" }}>
              {"\uD83C\uDFA4"}
            </button>
          )}
          {voiceSupported && voiceMode && (
            <button
              onClick={isListening ? stopListening : startListening}
              disabled={isSpeaking}
              style={{ background: isListening ? "#EF4444" : "#10B981", border: "none", borderRadius: "50%", width: 44, height: 44, color: "white", fontSize: 18, cursor: isSpeaking ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: isListening ? "0 0 0 4px #EF444433" : "none", transition: "all .2s", animation: isListening ? "pulse 1.5s infinite" : "none" }}>
              {isListening ? "\u23F9" : "\uD83C\uDFA4"}
            </button>
          )}
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !loading && sendMessage(input)}
            placeholder={voiceMode ? "Or type here..." : "Ask Lumi anything..."}
            style={{
              flex: 1, background: T.card, border: `1px solid ${T.border}`,
              borderRadius: 24, padding: "11px 16px", color: T.text,
              fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none",
            }}
          />
        <button
          onClick={() => !loading && sendMessage(input)}
          disabled={loading || !input.trim()}
          style={{
            background: loading ? T.border : "#FBBF24",
            border: "none", borderRadius: "50%", width: 44, height: 44,
            color: "#1A0A00", fontSize: 18, cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, fontWeight: 700, transition: "background 0.2s",
          }}
        >{"\u27A4"}</button>
        </div>
      </div>
    </div>
  );
}
